import { z } from "zod";

export const PASSWORD_REQUIREMENTS = [
  { id: "length", label: "Mínimo de 8 caracteres", test: (v: string) => v.length >= 8 },
  { id: "upper", label: "1 letra maiúscula", test: (v: string) => /[A-Z]/.test(v) },
  { id: "lower", label: "1 letra minúscula", test: (v: string) => /[a-z]/.test(v) },
  { id: "number", label: "1 número", test: (v: string) => /[0-9]/.test(v) },
] as const;

export const passwordSchema = z
  .string()
  .min(8, "A senha deve ter no mínimo 8 caracteres")
  .regex(/[A-Z]/, "A senha deve conter pelo menos uma letra maiúscula")
  .regex(/[a-z]/, "A senha deve conter pelo menos uma letra minúscula")
  .regex(/[0-9]/, "A senha deve conter pelo menos um número");

const OBVIOUS_WEAK = new Set([
  "12345678",
  "123456789",
  "1234567890",
  "password",
  "password1",
  "senha123",
  "senha1234",
  "qwerty123",
  "abcd1234",
  "abc12345",
  "11111111",
  "00000000",
]);

export function isObviouslyWeakPassword(value: string): boolean {
  const v = value.toLowerCase().trim();
  if (OBVIOUS_WEAK.has(v)) return true;
  // Sequential digits like 12345678 or repeated single char aaaaaaaa
  if (/^(.)\1{7,}$/.test(v)) return true;
  if (/^(0123456789|1234567890|abcdefgh|qwertyui)/.test(v)) return true;
  return false;
}

export function mapSupabasePasswordError(rawMessage: string): string {
  const msg = (rawMessage || "").toLowerCase();

  if (msg.includes("different from the old password") || msg.includes("same_password")) {
    return "A nova senha precisa ser diferente da senha atual.";
  }
  if (
    msg.includes("pwned") ||
    msg.includes("compromised") ||
    msg.includes("known to be weak") ||
    msg.includes("easy to guess") ||
    msg.includes("weak")
  ) {
    return "Essa senha apareceu em vazamentos de dados. Escolha uma senha mais forte e única.";
  }
  if (
    msg.includes("should be at least") ||
    msg.includes("at least 8 characters") ||
    msg.includes("password should contain")
  ) {
    return "A senha não atende aos requisitos mínimos (mínimo de 8 caracteres, com letra maiúscula, minúscula e número).";
  }
  if (
    msg.includes("for security purposes") ||
    msg.includes("rate limit") ||
    msg.includes("too many requests")
  ) {
    return "Muitas tentativas seguidas. Aguarde alguns segundos e tente de novo.";
  }
  if (msg.includes("reauthentication") || msg.includes("nonce")) {
    return "Por segurança, é preciso solicitar um novo link de recuperação.";
  }
  return `Falha ao atualizar senha: ${rawMessage}`;
}

export function isSessionError(rawMessage: string): boolean {
  const msg = (rawMessage || "").toLowerCase();
  return (
    msg.includes("session") ||
    msg.includes("auth") ||
    msg.includes("token") ||
    msg.includes("expired") ||
    (msg.includes("invalid") && !msg.includes("password"))
  );
}